import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCardIcon, PackageIcon, FileTextIcon, ClockIcon, CheckCircleIcon, XCircleIcon } from 'lucide-react';
interface PaymentRequest {
  id: string;
  paymentRequestId: string;
  type: 'order' | 'prescription' | 'other';
  referenceId: string;
  createdAt: string;
  amount: number;
  currency: string;
  status: 'pending' | 'paid' | 'expired' | 'cancelled';
}
export function PaymentRequestsSection() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  // Mock data - replace with API call
  const paymentRequests: PaymentRequest[] = [{
    id: '1',
    paymentRequestId: 'PR-2025-000123',
    type: 'order',
    referenceId: 'ORD-2025-001234',
    createdAt: '2025-01-15T10:30:00Z',
    amount: 450.0,
    currency: 'SAR',
    status: 'pending'
  }, {
    id: '2',
    paymentRequestId: 'PR-2025-000122',
    type: 'prescription',
    referenceId: 'RX-2025-000456',
    createdAt: '2025-01-14T14:20:00Z',
    amount: 280.5,
    currency: 'SAR',
    status: 'paid'
  }, {
    id: '3',
    paymentRequestId: 'PR-2025-000121',
    type: 'order',
    referenceId: 'ORD-2025-001233',
    createdAt: '2025-01-10T09:15:00Z',
    amount: 125.0,
    currency: 'SAR',
    status: 'expired'
  }];
  const filteredRequests = statusFilter === 'all' ? paymentRequests : paymentRequests.filter(req => req.status === statusFilter);
  const getStatusBadge = (status: string) => {
    const badges = {
      pending: {
        bg: 'bg-yellow-100',
        text: 'text-yellow-800',
        icon: ClockIcon,
        label: 'Pending'
      },
      paid: {
        bg: 'bg-green-100',
        text: 'text-green-800',
        icon: CheckCircleIcon,
        label: 'Paid'
      },
      expired: {
        bg: 'bg-gray-100',
        text: 'text-gray-800',
        icon: XCircleIcon,
        label: 'Expired'
      },
      cancelled: {
        bg: 'bg-red-100',
        text: 'text-red-800',
        icon: XCircleIcon,
        label: 'Cancelled'
      }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`} data-id="element-1003" data-mp="1wulxs8qshpl">
        <Icon className="w-3.5 h-3.5" data-id="element-1004" data-mp="arr4vci9l8qn" />
        {badge.label}
      </span>;
  };
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'order':
        return <PackageIcon className="w-4 h-4" data-id="element-1005" data-mp="kpfbazp8g4on" />;
      case 'prescription':
        return <FileTextIcon className="w-4 h-4" data-id="element-1006" data-mp="kncbxp26wz79" />;
      default:
        return <CreditCardIcon className="w-4 h-4" data-id="element-1007" data-mp="pngqvang9lvd" />;
    }
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  if (filteredRequests.length === 0) {
    return <div className="bg-white rounded-xl border border-gray-200 p-8 text-center" data-id="element-1008" data-mp="13uaeexz8layl">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1009" data-mp="13s7cbfkt7rdi">
          <CreditCardIcon className="w-8 h-8 text-gray-400" data-id="element-1010" data-mp="utc9mdgz4zey" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1011" data-mp="ur97ww6glhue">
          No Payment Requests
        </h3>
        <p className="text-sm text-gray-500" data-id="element-1012" data-mp="1rha5nkot38nu">
          {statusFilter === 'all' ? "You don't have any payment requests yet." : `No ${statusFilter} payment requests found.`}
        </p>
      </div>;
  }
  return <div className="space-y-4" data-id="element-1013" data-mp="1dkmw8ifo2q11">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6" data-id="element-1014" data-mp="1dlxygdtgrpq1">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-1015" data-mp="75cgx803qyxq">
          <div data-id="element-1016" data-mp="233me77kzoti">
            <h2 className="text-xl font-bold text-gray-900" data-id="element-1017" data-mp="26rgp8kuof9a">
              Payment Requests
            </h2>
            <p className="text-sm text-gray-500 mt-1" data-id="element-1018" data-mp="1pzjkqqc88lbj">
              View and manage your payment links and pending payments.
            </p>
          </div>
          {/* Status Filter */}
          <div className="flex items-center gap-2" data-id="element-1019" data-mp="1gylee1ebg1ps">
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-1020" data-mp="1lyzpxtf4ltsk">
              <option value="all" data-id="element-1021" data-mp="1zrpd416e54r9">All Status</option>
              <option value="pending" data-id="element-1022" data-mp="1zqxnd6zh618w">Pending</option>
              <option value="paid" data-id="element-1023" data-mp="24phcthda1v60">Paid</option>
              <option value="expired" data-id="element-1024" data-mp="24sdd3cclkvxm">Expired</option>
              <option value="cancelled" data-id="element-1025" data-mp="obr5m1qv2qs5">Cancelled</option>
            </select>
          </div>
        </div>
      </div>
      {/* Desktop Table */}
      <div className="hidden md:block bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-1026" data-mp="t90vtjm2r6h1">
        <table className="w-full" data-id="element-1027" data-mp="ltp56aw8hxbc">
          <thead className="bg-gray-50 border-b border-gray-200" data-id="element-1028" data-mp="gt14sym4844g">
            <tr data-id="element-1029" data-mp="guue6lo452g5">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1030" data-mp="usaoi89j5gd6">
                Payment Request
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1031" data-mp="ptzbjonxupqq">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1032" data-mp="3u7xsb3fzlvw4">
                Reference
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1033" data-mp="bupyuyyxydri">
                Created
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1034" data-mp="3fhq8oke8u6tu">
                Amount
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1035" data-mp="3ahc97qqpk57f">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1036" data-mp="1jelkreqce8ok">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200" data-id="element-1037" data-mp="1t5ogs24ezxgz">
            {filteredRequests.map(request => <tr key={request.id} className="hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => navigate(`/account/payment-requests/${request.id}`)} data-id="element-1038" data-mp="vi7pt0ameldt">
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1039" data-mp="15hyhku7sqma7">
                  <div className="text-sm font-medium text-gray-900" data-id="element-1040" data-mp="19iqubxfvn9b8">
                    {request.paymentRequestId}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1041" data-mp="2kvta4qa5juuc">
                  <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-1042" data-mp="2kyfhaot4s8c1">
                    {getTypeIcon(request.type)}
                    <span className="capitalize" data-id="element-1043" data-mp="57nnylgqq1ur">{request.type}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1044" data-mp="fvyyfd8g46q">
                  <div className="text-sm text-gray-600" data-id="element-1045" data-mp="ecj2pvhnv5h">
                    {request.referenceId}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1046" data-mp="3moykyjse5h11">
                  <div className="text-sm text-gray-600" data-id="element-1047" data-mp="3hoso8p79l4c3">
                    {formatDate(request.createdAt)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1048" data-mp="3do0hq3ps1dji">
                  <div className="text-sm font-medium text-gray-900" data-id="element-1049" data-mp="38ontbej9byet">
                    {request.amount.toFixed(2)} {request.currency}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1050" data-mp="16wrik74wysl3">
                  {getStatusBadge(request.status)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1051" data-mp="fr5dbp8u865o">
                  <button onClick={e => {
                e.stopPropagation();
                navigate(`/account/payment-requests/${request.id}`);
              }} className="text-sm font-medium text-[#1E2B55] hover:text-[#162041]" data-id="element-1052" data-mp="kqib3518yxn1">
                    View Details
                  </button>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
      {/* Mobile Cards */}
      <div className="md:hidden space-y-3" data-id="element-1053" data-mp="10690qf1c3rw">
        {filteredRequests.map(request => <div key={request.id} onClick={() => navigate(`/account/payment-requests/${request.id}`)} className="bg-white rounded-xl border border-gray-200 p-4 active:bg-gray-50 transition-colors" data-id="element-1054" data-mp="3ja8hnruhj66l">
            <div className="flex items-start justify-between mb-3" data-id="element-1055" data-mp="1mg3ux3do12bs">
              <div data-id="element-1056" data-mp="36xw9nc2ch7uh">
                <div className="text-sm font-semibold text-gray-900 mb-1" data-id="element-1057" data-mp="36xd3stpyy9wt">
                  {request.paymentRequestId}
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500" data-id="element-1058" data-mp="320cwyglap34n">
                  {getTypeIcon(request.type)}
                  <span className="capitalize" data-id="element-1059" data-mp="3gpium04hilaf">{request.type}</span>
                  <span data-id="element-1060" data-mp="3bvem3cfkm578">•</span>
                  <span data-id="element-1061" data-mp="3bv4rtcnhtbvq">{request.referenceId}</span>
                </div>
              </div>
              {getStatusBadge(request.status)}
            </div>
            <div className="flex items-center justify-between pt-3 border-t border-gray-100" data-id="element-1062" data-mp="3kohvn9n3hxwl">
              <div className="text-xs text-gray-500" data-id="element-1063" data-mp="3fuv6k2dgyvc">
                {formatDate(request.createdAt)}
              </div>
              <div className="text-sm font-semibold text-gray-900" data-id="element-1064" data-mp="3uo8ol6w0c4uk">
                {request.amount.toFixed(2)} {request.currency}
              </div>
            </div>
          </div>)}
      </div>
    </div>;
}